import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from '../components/ui/sonner';
import { Upload, Download, QrCode, ArrowLeft } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PermissionDetail = () => {
  const { permissionNumber } = useParams();
  const navigate = useNavigate();
  const { t, isRtl } = useLanguage();
  const [permission, setPermission] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [issueDate, setIssueDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [uploading, setUploading] = useState(false);
  const [qrCode, setQrCode] = useState(null);

  useEffect(() => {
    fetchPermissionDetails();
  }, [permissionNumber]);

  const fetchPermissionDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const [permResponse, docsResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/permissions/${permissionNumber}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${BACKEND_URL}/api/documents/permission/${permissionNumber}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setPermission(permResponse.data);
      setDocuments(docsResponse.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        toast.error(t.sessionExpired);
        navigate('/login');
      } else {
        toast.error(t.somethingWentWrong);
      }
    } finally {
      setLoading(false);
    }
  };

  // Local translations for page-specific text
  const texts = {
    en: {
      manageTanks: "Manage tanks and documents",
      quickActions: "Quick Actions",
      noTanksAssigned: "No tanks assigned",
      documentHistory: "Document History",
      noDocsUploaded: "No documents uploaded yet",
      issue: "Issue",
      expiry: "Expiry",
      pdfDocument: "PDF Document",
      upload: "Upload",
      onlyPdfAllowed: "Only PDF files are allowed",
      fillAllFields: "Please fill all fields",
      uploadFailed: "Upload failed",
      downloadFailed: "Failed to download document",
      qrDownloaded: "QR Code downloaded",
      qrDownloadFailed: "Failed to download QR code"
    },
    ar: {
      manageTanks: "إدارة الخزانات والمستندات",
      quickActions: "إجراءات سريعة",
      noTanksAssigned: "لا توجد خزانات مخصصة",
      documentHistory: "سجل المستندات",
      noDocsUploaded: "لم يتم رفع مستندات بعد",
      issue: "الإصدار",
      expiry: "الانتهاء",
      pdfDocument: "مستند PDF",
      upload: "رفع",
      onlyPdfAllowed: "ملفات PDF فقط مسموح بها",
      fillAllFields: "يرجى ملء جميع الحقول",
      uploadFailed: "فشل الرفع",
      downloadFailed: "فشل تحميل المستند",
      qrDownloaded: "تم تحميل رمز QR",
      qrDownloadFailed: "فشل تحميل رمز QR"
    }
  };
  const txt = texts[isRtl ? 'ar' : 'en'];

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && !selectedFile.name.toLowerCase().endsWith('.pdf')) {
      toast.error(txt.onlyPdfAllowed);
      return;
    }
    setFile(selectedFile);
  };

  const handleUpload = async () => {
    if (!file || !issueDate || !expiryDate) {
      toast.error(txt.fillAllFields);
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('permission_number', permissionNumber);
      formData.append('issue_date', new Date(issueDate).toISOString());
      formData.append('expiry_date', new Date(expiryDate).toISOString());

      const response = await axios.post(`${BACKEND_URL}/api/documents/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      setQrCode(response.data.qr_code);
      toast.success(t.documentUploaded);
      setUploadDialogOpen(false);
      setFile(null);
      setIssueDate('');
      setExpiryDate('');
      fetchPermissionDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || txt.uploadFailed);
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadDocument = async (documentHash, filename) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/documents/download/${documentHash}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error(txt.downloadFailed);
    }
  };

  const handleDownloadQR = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/qr/${permissionNumber}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const qrData = response.data.qr_code;
      const link = document.createElement('a');
      link.href = `data:image/png;base64,${qrData}`;
      link.download = `permission-${permissionNumber}-qr.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success(txt.qrDownloaded);
    } catch (error) {
      toast.error(txt.qrDownloadFailed);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">{t.loading}</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="permission-detail-page" className="space-y-6">
        <div className={`flex items-center gap-4 mb-6 ${isRtl ? 'flex-row-reverse' : ''}`}>
          <Button
            data-testid="back-button"
            variant="outline"
            onClick={() => navigate('/permissions')}
            className={`flex items-center gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}
          >
            <ArrowLeft className={`w-4 h-4 ${isRtl ? 'rotate-180' : ''}`} />
            {t.backToPermissions}
          </Button>
          <div className={isRtl ? 'text-right' : ''}>
            <h1 className="text-4xl font-bold text-slate-900">{t.permissionDetails} #{permissionNumber}</h1>
            <p className="text-slate-600 mt-1">{txt.manageTanks}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="p-6 lg:col-span-2 bg-white border border-slate-200">
            <div className={`flex justify-between items-center mb-6 ${isRtl ? 'flex-row-reverse' : ''}`}>
              <h2 className="text-2xl font-bold text-slate-900">{t.assignedTanks}</h2>
              <span className="text-slate-600 font-medium">
                {permission.tank_ids.length} {t.tanks}
              </span>
            </div>

            {permission.tank_ids.length === 0 ? (
              <p className="text-center text-slate-500 py-8">{txt.noTanksAssigned}</p>
            ) : (
              <div className={`flex flex-wrap gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
                {permission.tank_ids.map((tankId) => (
                  <div 
                    key={tankId} 
                    className="bg-slate-900 text-white rounded-lg px-4 py-2 font-mono font-semibold text-sm"
                  >
                    {tankId}
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card className="p-6 bg-white border border-slate-200">
            <h2 className={`text-2xl font-bold text-slate-900 mb-4 ${isRtl ? 'text-right' : ''}`}>{txt.quickActions}</h2>
            <div className="space-y-3">
              <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    data-testid="upload-document-button"
                    className={`w-full bg-slate-900 hover:bg-slate-800 text-white ${isRtl ? 'flex-row-reverse' : ''}`}
                  >
                    <Upload className={`w-4 h-4 ${isRtl ? 'ml-2' : 'mr-2'}`} />
                    {t.uploadDocument}
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white">
                  <DialogHeader>
                    <DialogTitle className={`text-2xl font-bold ${isRtl ? 'text-right' : ''}`}>{t.uploadDocument}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label htmlFor="file" className={isRtl ? 'block text-right' : ''}>{txt.pdfDocument}</Label>
                      <Input
                        id="file"
                        data-testid="file-input"
                        type="file"
                        accept=".pdf"
                        onChange={handleFileChange}
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="issue-date" className={isRtl ? 'block text-right' : ''}>{t.issueDate}</Label>
                      <Input
                        id="issue-date"
                        data-testid="issue-date-input"
                        type="date"
                        value={issueDate}
                        onChange={(e) => setIssueDate(e.target.value)}
                        className={`mt-2 ${isRtl ? 'text-right' : ''}`}
                      />
                    </div>
                    <div>
                      <Label htmlFor="expiry-date" className={isRtl ? 'block text-right' : ''}>{t.expiryDate}</Label>
                      <Input
                        id="expiry-date"
                        data-testid="expiry-date-input"
                        type="date"
                        value={expiryDate}
                        onChange={(e) => setExpiryDate(e.target.value)}
                        className={`mt-2 ${isRtl ? 'text-right' : ''}`}
                      />
                    </div>
                    <Button
                      data-testid="upload-submit-button"
                      onClick={handleUpload}
                      disabled={uploading}
                      className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                    >
                      {uploading ? t.uploading : txt.upload}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              <Button
                data-testid="download-qr-button"
                onClick={handleDownloadQR}
                variant="outline"
                className={`w-full ${isRtl ? 'flex-row-reverse' : ''}`}
              >
                <QrCode className={`w-4 h-4 ${isRtl ? 'ml-2' : 'mr-2'}`} />
                {t.downloadQrCode}
              </Button>
            </div>

            {permission.document_status && (
              <div className={`mt-6 p-4 bg-slate-50 rounded-lg ${isRtl ? 'text-right' : ''}`}>
                <p className="text-sm text-slate-600 mb-2">{t.documentStatus}</p>
                <span className={`status-badge ${permission.document_status}`}>
                  {t[permission.document_status] || permission.document_status.replace('_', ' ')}
                </span>
              </div>
            )}
          </Card>
        </div>

        <Card className="p-6 bg-white border border-slate-200">
          <h2 className={`text-2xl font-bold text-slate-900 mb-4 ${isRtl ? 'text-right' : ''}`}>{txt.documentHistory}</h2>
          {documents.length === 0 ? (
            <p className="text-center text-slate-500 py-8">{txt.noDocsUploaded}</p>
          ) : (
            <div className="space-y-3">
              {documents.map((doc) => (
                <div
                  key={doc.document_hash}
                  data-testid={`document-${doc.document_hash}`}
                  className={`flex items-center justify-between p-4 border border-slate-200 rounded-lg ${isRtl ? 'flex-row-reverse' : ''}`}
                >
                  <div className={`flex-1 ${isRtl ? 'text-right' : ''}`}>
                    <p className="font-medium text-slate-900">{doc.original_filename}</p>
                    <div className={`flex gap-4 mt-1 text-sm text-slate-600 ${isRtl ? 'flex-row-reverse' : ''}`}>
                      <span>{txt.issue}: {new Date(doc.issue_date).toLocaleDateString(isRtl ? 'ar-SA' : 'en-US')}</span>
                      <span>{txt.expiry}: {new Date(doc.expiry_date).toLocaleDateString(isRtl ? 'ar-SA' : 'en-US')}</span>
                    </div>
                  </div>
                  <div className={`flex items-center gap-3 ${isRtl ? 'flex-row-reverse' : ''}`}>
                    <span className={`status-badge ${doc.status}`}>
                      {t[doc.status] || doc.status}
                    </span>
                    {doc.is_active && (
                      <Button
                        data-testid={`download-doc-${doc.document_hash}`}
                        size="sm"
                        onClick={() => handleDownloadDocument(doc.document_hash, doc.original_filename)}
                        className={`bg-slate-900 hover:bg-slate-800 text-white ${isRtl ? 'flex-row-reverse' : ''}`}
                      >
                        <Download className={`w-4 h-4 ${isRtl ? 'ml-1' : 'mr-1'}`} />
                        {t.downloadDocument}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
};

export default PermissionDetail;